import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, QrCode, Smartphone } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const domains = [
  {
    url: 'https://xtreamcube.com',
    name: 'xtreamcube.com',
    badge: 'OFFICIAL',
    badgeVariant: 'default',
    status: 'Live',
    statusColor: 'text-green-400'
  },
  {
    url: 'https://xtreamcube.shop',
    name: 'xtreamcube.shop',
    badge: 'BACKUP 1',
    badgeVariant: 'secondary',
    status: 'Online',
    statusColor: 'text-green-400'
  },
  {
    url: 'https://xtreamcube.xyz',
    name: 'xtreamcube.xyz',
    badge: 'BACKUP 2',
    badgeVariant: 'secondary',
    status: 'Online',
    statusColor: 'text-green-400'
  },
  {
    url: 'https://xtreamcube.online',
    name: 'xtreamcube.online',
    badge: 'BACKUP 3',
    badgeVariant: 'secondary',
    status: 'Online',
    statusColor: 'text-green-400'
  }
];

const DomainCard = ({ domain, index }) => {
  const [showQR, setShowQR] = useState(false);
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(domain.url)}`;

  // Handle PWA install prompt for saving shortcut
  const handleSaveShortcut = async () => {
    // Check if PWA install prompt is available
    if (window.deferredPWA) {
      try {
        await window.deferredPWA.prompt();
        const { outcome } = await window.deferredPWA.userChoice;
        if (outcome === 'accepted') {
          alert('✅ Shortcut saved to your home screen!');
        } else {
          alert('You can save this shortcut anytime using your browser menu.');
        }
      } catch (err) {

        showManualInstructions();
      }
    } else {
      showManualInstructions();
    }
  };

  // Show manual instructions based on platform
  const showManualInstructions = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isMobile = isIOS || isAndroid;

    if (isIOS) {
      alert(`📱 Save Shortcut on iOS:\n\n1. Tap the Share button (box with arrow) at the bottom\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to confirm\n\nThis will create an app icon for quick access!`);
    } else if (isAndroid) {
      alert(`📱 Save Shortcut on Android:\n\n1. Tap the menu icon (⋮) in your browser\n2. Tap "Add to Home screen" or "Install app"\n3. Confirm to save\n\nThis will create an app icon for quick access!`);
    } else {
      // Desktop
      alert(`💻 Save Shortcut:\n\n• Chrome/Edge: Click the install icon (⊕) in the address bar, or use menu → "Install XtreamGate"\n• Firefox: Bookmark this page (Ctrl+D)\n• Safari: Add to favorites or use File → Add to Dock\n\nFor mobile access, scan the QR code with your phone!`);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{
          duration: 0.5,
          delay: index * 0.15,
          ease: "easeOut"
        }}
        className="group"
      >
        <motion.a
          href={domain.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block relative glass rounded-xl p-6 transition-all duration-300"
          whileHover={{ y: -4, scale: 1.02 }}
          style={{ boxShadow: 'var(--shadow-glass)' }}
        >
          {/* Glow effect on hover */}
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          <div className="relative space-y-4">
            {/* Header with badge and status */}
            <div className="flex items-center justify-between">
              <Badge
                variant={domain.badgeVariant}
                className={domain.badge === 'OFFICIAL' ? 'bg-primary/20 text-primary border-primary/30' : ''}
              >
                {domain.badge}
              </Badge>
              <div className="flex items-center gap-2">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-2 h-2 rounded-full bg-green-400"
                />
                <span className={`text-sm font-medium ${domain.statusColor}`}>
                  {domain.status}
                </span>
              </div>
            </div>

            {/* Domain name */}
            <div className="space-y-2">
              <p className="text-2xl font-semibold text-foreground group-hover:text-primary transition-colors">
                {domain.name}
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-muted-foreground flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Visit site
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSaveShortcut();
                  }}
                  className="text-primary hover:text-primary/80 transition-colors"
                  aria-label="Save shortcut"
                  title="Save Shortcut"
                >
                  <Smartphone className="w-5 h-5" />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowQR(true);
                  }}
                  className="text-primary hover:text-primary/80 transition-colors"
                  aria-label="Show QR code"
                  title="Show QR Code"
                >
                  <QrCode className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </motion.a>
      </motion.div>

      {/* QR Code Dialog */}
      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="sm:max-w-md glass border-border">
          <DialogHeader>
            <DialogTitle className="text-center">Scan to Access</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="bg-white p-4 rounded-lg">
              <img
                src={qrUrl}
                alt={`QR code for ${domain.name}`}
                className="w-48 h-48"
              />
            </div>
            <p className="text-center text-primary font-medium">{domain.name}</p>
            <p className="text-center text-sm text-muted-foreground">
              Scan with your mobile device to access instantly
            </p>

            {/* Save Shortcut Button */}
            <button
              onClick={handleSaveShortcut}
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-all duration-200 font-medium"
            >
              <Smartphone className="w-4 h-4" />
              Save Shortcut
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const DomainGrid = () => {
  return (
    <section id="domains" className="py-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 space-y-3"
        >
          <h2 className="text-3xl sm:text-4xl font-bold">
            Official XtreamCube Domains
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            All sites below are official XtreamCube properties.
          </p>
        </motion.div>

        {/* Domain grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {domains.map((domain, index) => (
            <DomainCard key={domain.name} domain={domain} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};